import React, {FC, useState} from 'react';
import styled from 'styled-components';
import {ElementTrackingContextProvider} from '../../../tracking/context/ElementTrackingContext';
import {SectionTrackingContextProvider} from '../../../tracking/context/SectionTrackingContext';
import {GenericPromotionTracker} from '../../../tracking/trackerComponents/GenericPromotionTracker';
import {MissionBadgeType} from '../../../types/missions';
import {SimpleButton} from '../../../ui/Buttons/SimpleButton';
import EmptyState from '../../../ui/CodBankData/components/EmptyState/EmptyState';
import CodBankDataFormContent, {
  BICTranslations,
} from '../../../ui/CodBankData/components/FormContent';
import {useBankDetailsTranslationContext} from '../../../ui/CodBankData/context/CodBankDataTranslation.context';
import useBankDataForm from '../../../ui/CodBankData/hooks/useBankDataForm';
import {
  CODPaymentInformation,
  UpdateOrCreateBankInformation,
} from '../../../ui/CodBankData/hooks/useCodPaymentInformation';
import {isBankDataEntered} from '../../../ui/CodBankData/utils/isBankDataEntered';
import AccountAreaPageLayout from '../../../ui/Layouts/AccountAreaPageLayout';
import {AccountAreaNavigationProps} from '../../../ui/Navigation/AccountAreaNavigation/AccountAreaNavigation';
import StatusToast from '../../../ui/StatusToast/StatusToast';
import {bordersColor} from '../../../ui/constants/colors';
import {
  FullscreenLoading,
  LoadingContainer,
} from '../../../ui/utilityComponents/Loading/Loading';
import {useUniqueId} from '../../../utils/hooks/useUniqueId';
import {BANK_DATA_ROUTE} from '../routes';
import {useBankDataPageTranslationContext} from './context/TranslationProvider';

export interface BankDataPageProps extends AccountAreaNavigationProps {
  updateOrCreateBankInformation: UpdateOrCreateBankInformation;
  codPaymentInformation?: CODPaymentInformation;
  bicTranslations: BICTranslations;
  loading: boolean;
  MissionBadge: MissionBadgeType;
}
const BankDataPageUI: FC<BankDataPageProps> = function BankDataPageUI({
  updateOrCreateBankInformation,
  codPaymentInformation,
  bicTranslations,
  loading,
  MissionBadge,
  ...navigationProps
}) {
  const [editMode, setEditMode] = useState(false);
  const formId = useUniqueId('bank-form-');

  const {
    submitHandler,
    initialFormValues,
    reset,
    handleSubmit,
    register,
    errors,
    setValue,
    watch,
    saveBankDataStatus,
    resetSaveBankDataStatus,
    isSaveSuccess,
    formState,
  } = useBankDataForm(
    updateOrCreateBankInformation,
    codPaymentInformation,
    'onChange',
    editMode,
  );

  const {isValid, isDirty} = formState;

  const {pageTitle, successToastMessage, errorToastMessage} =
    useBankDataPageTranslationContext();

  const {cancelButtonText, saveBankDataText, editButtonText} =
    useBankDetailsTranslationContext();

  React.useEffect(() => {
    if (isSaveSuccess) {
      setEditMode(false);
      setValue('iban', codPaymentInformation?.bankData.iban);
      setValue('bic', codPaymentInformation?.bankData.bic);
    }
  }, [
    codPaymentInformation?.bankData.bic,
    codPaymentInformation?.bankData.iban,
    isSaveSuccess,
    setValue,
  ]);

  const activateEditMode = () => {
    setEditMode(true);
    setValue('iban', '');
    setValue('bic', '');
  };
  const cancelEditMode = () => {
    setEditMode(false);
    setValue('iban', codPaymentInformation?.bankData.iban);
    setValue('bic', codPaymentInformation?.bankData.bic);
  };

  const cancelBankDataEdit = () => {
    cancelEditMode();
    reset(codPaymentInformation?.bankData || initialFormValues);
    resetSaveBankDataStatus();
  };

  const bankDataForm = (
    <>
      {saveBankDataStatus === 'progress' && <FullscreenLoading />}
      <form
        onSubmit={handleSubmit(submitHandler)}
        id={formId}
        method="POST"
        autoComplete="on"
      >
        <BankDataFormWrapper>
          <CodBankDataFormContent
            disabled={saveBankDataStatus === 'progress' || !editMode}
            errors={errors}
            register={register}
            watch={watch}
            bicTranslations={bicTranslations}
            singleInputPerLine={true}
          />
        </BankDataFormWrapper>
        <BankDataButtonWrapper $editMode={editMode}>
          {editMode ? (
            <>
              <StyledSimpleButton
                variant="default"
                buttonType="secondary"
                data-testid="cancelButton"
                onClick={cancelBankDataEdit}
              >
                {cancelButtonText}
              </StyledSimpleButton>
              <ElementTrackingContextProvider>
                <GenericPromotionTracker
                  name={'save_bank_data'}
                  id={'save_bank_data'}
                  source={'CTA' as any}
                >
                  <StyledSimpleButton
                    data-testid="submitButton"
                    type="submit"
                    buttonType={'primary'}
                    disabled={!isValid && isDirty}
                  >
                    {saveBankDataText}
                  </StyledSimpleButton>
                </GenericPromotionTracker>
              </ElementTrackingContextProvider>
            </>
          ) : (
            <StyledSimpleButton
              data-testid="editButton"
              buttonType={'primary'}
              onClick={activateEditMode}
            >
              {editButtonText}
            </StyledSimpleButton>
          )}
        </BankDataButtonWrapper>
      </form>
    </>
  );

  let pageContent;
  if (
    !isBankDataEntered(codPaymentInformation) &&
    !editMode &&
    saveBankDataStatus !== 'progress'
  ) {
    pageContent = <EmptyState setEditMode={activateEditMode} />;
  } else {
    pageContent = bankDataForm;
  }

  return (
    <>
      <SectionTrackingContextProvider>
        <AccountAreaPageLayout
          {...navigationProps}
          activePage={BANK_DATA_ROUTE}
          pageTitle={editMode ? pageTitle.form : pageTitle.default}
          onBackClick={editMode ? cancelBankDataEdit : undefined}
          MissionBadge={MissionBadge}
        >
          {loading ? <LoadingContainer /> : pageContent}
        </AccountAreaPageLayout>
      </SectionTrackingContextProvider>

      <StatusToast
        data-testid="ToastNotification"
        type={saveBankDataStatus === 'success' ? 'SUCCESS' : 'FAILURE'}
        isShown={
          saveBankDataStatus === 'success' || saveBankDataStatus === 'error'
        }
        autoHide
      >
        {saveBankDataStatus === 'success'
          ? successToastMessage
          : errorToastMessage}
      </StatusToast>
    </>
  );
};

export default BankDataPageUI;

const BankDataFormWrapper = styled.div`
  max-width: 539px;
  border-radius: 5px;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.04), 0 10px 15px 0 rgba(0, 0, 0, 0.04);
  border: 1px solid ${bordersColor};
  padding: 32px;
`;

const BankDataButtonWrapper = styled.div<{$editMode?: boolean}>`
  max-width: calc(539px);
  display: flex;
  gap: 20px;
  justify-content: ${({$editMode}) =>
    $editMode ? 'space-between' : 'flex-end'};
  margin-top: 40px;
`;

const StyledSimpleButton = styled(SimpleButton)`
  width: auto;
`;
